/**
 * weekly-limit.js (FINAL)
 *
 * Runs ONLY where Shopify dynamic checkout ("Buy Now") exists:
 *  - Shows message below Buy Now: "You can add X more items this week."
 *  - Disables Buy Now when weeklyUsed + selectedQty > weeklyLimit
 *
 * Cart: never injects messaging (no dynamic checkout container there).
 *
 * Requires Liquid:
 * <script id="weekly-limit-config" type="application/json">
 *   {"weeklyLimit":3,"weeklyUsed":1,"loggedIn":true}
 * </script>
 */

(function () {
  const CONFIG_ID = "weekly-limit-config";
  const MESSAGE_ID = "weekly-weeklylimit-message";
  const DISABLED_CLASS = "weekly-limit--disabled";
  const BOUND_ATTR = "data-weekly-limit-bound";
  const FORM_BOUND_ATTR = "data-weekly-form-bound";

  // Retry for themes that inject dynamic checkout late on PDP
  const MAX_RETRIES = 60;
  const RETRY_DELAY_MS = 200;

  function readConfig() {
    const el = document.getElementById(CONFIG_ID);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || "{}");
    } catch (_e) {
      return null;
    }
  }

  function toPosInt(value, fallback) {
    const n = parseInt(value, 10);
    return Number.isFinite(n) && n >= 0 ? n : fallback;
  }

  function findQtyInput() {
    return (
      document.querySelector('input[name="quantity"]') ||
      document.querySelector("quantity-input input") ||
      document.querySelector('[data-quantity-input] input') ||
      document.querySelector('input[id*="Quantity"]') ||
      null
    );
  }

  function findBuyNowButton() {
    return document.querySelector(".shopify-payment-button__button");
  }

  function findPaymentContainer() {
    return document.querySelector(".shopify-payment-button");
  }

  function removeMessage() {
    const msg = document.getElementById(MESSAGE_ID);
    if (msg) msg.remove();
  }

  function ensureMessage(anchorEl) {
    let msg = document.getElementById(MESSAGE_ID);
    if (msg) return msg;

    msg = document.createElement("div");
    msg.id = MESSAGE_ID;
    msg.setAttribute("role", "status");
    msg.setAttribute("aria-live", "polite");

    // Basic styling (you can move to CSS if desired)
    msg.style.marginTop = "10px";
    msg.style.fontSize = "0.95rem";
    msg.style.lineHeight = "1.35";
    msg.style.padding = "10px 12px";
    msg.style.borderRadius = "8px";
    msg.style.border = "1px solid rgba(0,0,0,0.12)";
    msg.style.background = "rgba(0,0,0,0.03)";

    if (anchorEl && anchorEl.parentNode) {
      if (anchorEl.nextSibling) {
        anchorEl.parentNode.insertBefore(msg, anchorEl.nextSibling);
      } else {
        anchorEl.parentNode.appendChild(msg);
      }
    } else {
      document.body.appendChild(msg);
    }
    return msg;
  }

  function setBuyNowDisabled(btn, disabled) {
    if (!btn) return;

    if (disabled) {
      btn.disabled = true;
      btn.classList.add(DISABLED_CLASS);
      btn.style.pointerEvents = "none";
      btn.style.opacity = "0.45";
      btn.style.cursor = "not-allowed";
      btn.setAttribute("aria-disabled", "true");
    } else {
      btn.disabled = false;
      btn.classList.remove(DISABLED_CLASS);
      btn.style.pointerEvents = "";
      btn.style.opacity = "";
      btn.style.cursor = "";
      btn.removeAttribute("aria-disabled");
    }
  }

  function updateUI(config) {
    if (!config) return;

    const paymentContainer = findPaymentContainer();
    const buyNowBtn = findBuyNowButton();

    // ✅ ONLY run messaging where Buy Now exists (PDP).
    // On cart (or anywhere else), remove message if it exists and exit.
    if (!paymentContainer && !buyNowBtn) {
      removeMessage();
      return;
    }

    const loggedIn = !!config.loggedIn;
    const limit = toPosInt(config.weeklyLimit, 0);
    const used = toPosInt(config.weeklyUsed, 0);

    // Not logged in → no message, no blocking
    if (!loggedIn) {
      removeMessage();
      setBuyNowDisabled(buyNowBtn, false);
      return;
    }

    const qtyInput = findQtyInput();
    const selectedQty = toPosInt(qtyInput ? qtyInput.value : "1", 1);

    const remaining = Math.max(0, limit - used);
    const wouldTotal = used + selectedQty;
    const exceeds = wouldTotal > limit;

    // Anchor message directly under dynamic checkout container/button
    const anchor = paymentContainer || buyNowBtn;
    const msgEl = ensureMessage(anchor);

    if (remaining <= 0) {
      msgEl.textContent =
        "You’ve already reached our limit of 3 items per week! Limits reset on Thursdays.";
      msgEl.style.borderColor = "rgba(176,0,32,0.35)";
      msgEl.style.background = "rgba(176,0,32,0.06)";
      setBuyNowDisabled(buyNowBtn, true);
      return;
    }

    // Default PDP message
    msgEl.textContent =
      "Mohawk IT Resale allows purchases of up to 3 items per week. You still have " +
      remaining +
      " item" +
      (remaining === 1 ? "" : "s") +
      " allowed for purchase this week - limits reset every Thursday!";
    msgEl.style.borderColor = "rgba(0,0,0,0.12)";
    msgEl.style.background = "rgba(0,0,0,0.03)";

    // If qty exceeds limit, show reduce message + disable Buy Now (PDP only)
    if (exceeds) {
      const overBy = wouldTotal - limit;
      msgEl.textContent =
        "Mohawk IT Resale allows up to 3 items per week. You still have " +
      remaining + " more item" +
      (remaining === 1 ? "" : "s") +
      " this week. Reduce your order quantity by " +
        overBy +
        " to stay within your weekly limit. Limits reset every Thursday!";
      msgEl.style.borderColor = "rgba(176,0,32,0.35)";
      msgEl.style.background = "rgba(176,0,32,0.06)";
      setBuyNowDisabled(buyNowBtn, true);
    } else {
      setBuyNowDisabled(buyNowBtn, false);
    }
  }

  function bindQuantityHandlers() {
    const paymentContainer = findPaymentContainer();
    const buyNowBtn = findBuyNowButton();

    // Only bind on PDP where Buy Now exists
    if (!paymentContainer && !buyNowBtn) return;

    const qtyInput = findQtyInput();
    if (!qtyInput) return;

    if (qtyInput.getAttribute(BOUND_ATTR) === "1") return;
    qtyInput.setAttribute(BOUND_ATTR, "1");

    const onChange = () => updateUI(readConfig());

    qtyInput.addEventListener("input", onChange, { passive: true });
    qtyInput.addEventListener("change", onChange, { passive: true });

    // +/- buttons
    const qtyRoot =
      qtyInput.closest("quantity-input") ||
      qtyInput.closest("[data-quantity-input]") ||
      qtyInput.parentElement;

    if (qtyRoot) {
      qtyRoot.addEventListener(
        "click",
        function (e) {
          const t = e.target;
          if (!t) return;
          if (t.matches("button") || t.closest("button")) {
            setTimeout(onChange, 0);
          }
        },
        { passive: true }
      );
    }

    // Variant changes can re-render quantities/buttons
    const productForm = qtyInput.closest('form[action*="/cart/add"]') || null;
    if (productForm && productForm.getAttribute(FORM_BOUND_ATTR) !== "1") {
      productForm.setAttribute(FORM_BOUND_ATTR, "1");
      productForm.addEventListener(
        "change",
        () => {
          bindQuantityHandlers();
          updateUI(readConfig());
        },
        { passive: true }
      );
    }
  }

  function init() {
    const cfg = readConfig();
    if (!cfg) return;

    updateUI(cfg);
    bindQuantityHandlers();

    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      // Keep cleaning up message if we’re not on PDP
      updateUI(readConfig());

      // Bind once Buy Now appears (late injection)
      bindQuantityHandlers();

      if (tries >= MAX_RETRIES) clearInterval(timer);
    }, RETRY_DELAY_MS);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();